<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div>
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>
    <b-container fluid>
      <!-- Modern Title Section -->
      <div class="title-section">
        <div class="title-container">
          <div class="title-icon-wrapper">
            <feather-icon
              icon="MapPinIcon"
              size="28"
              class="title-icon"
            />
          </div>
          <div class="title-text-wrapper">
            <h2 class="main-title">
              <span class="title-word">Suivi</span>
              <span class="title-word">des</span>
              <span class="title-word">Commandes</span>
              <span class="title-word">par</span>
              <span class="title-word highlight">Gouvernorat</span>
            </h2>
            <p class="subtitle">
              <span class="subtitle-icon">📊</span>
              Suivez le trafic et les performances par gouvernorat pour la période de votre choix
            </p>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <b-card
        no-body
        class="filter-card shadow-sm border-0 mb-4"
      >
        <b-card-body>
          <b-row align-v="center">
            <b-col
              md="4"
              class="mb-2 mb-md-0"
            >
              <b-form-group
                label="Date de début"
                label-class="filter-label"
              >
                <b-form-datepicker
                  v-model="fromDate"
                  :max="toDate || undefined"
                  locale="fr"
                  placeholder="Sélectionner une date"
                  class="filter-datepicker"
                  today-button
                  reset-button
                  close-button
                />
              </b-form-group>
            </b-col>
            <b-col
              md="4"
              class="mb-2 mb-md-0"
            >
              <b-form-group
                label="Date de fin"
                label-class="filter-label"
              >
                <b-form-datepicker
                  v-model="toDate"
                  :min="fromDate || undefined"
                  locale="fr"
                  placeholder="Sélectionner une date"
                  class="filter-datepicker"
                  today-button
                  reset-button
                  close-button
                />
              </b-form-group>
            </b-col>
            <b-col
              md="4"
              class="d-flex align-items-end justify-content-md-end"
            >
              <b-button
                variant="primary"
                class="mr-2 filter-btn"
                :disabled="isLoading"
                @click="fetchAnalytics"
              >
                <feather-icon
                  icon="FilterIcon"
                  size="16"
                  class="mr-1"
                />
                Appliquer
              </b-button>
              <b-button
                variant="outline-secondary"
                class="filter-btn"
                :disabled="isLoading"
                @click="resetFilters"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="16"
                  class="mr-1"
                />
                Réinitialiser
              </b-button>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col>
              <div class="preset-group">
                <span class="preset-label">Préréglages :</span>
                <b-button-group size="sm">
                  <b-button
                    v-for="preset in presets"
                    :key="preset.key"
                    variant="outline-primary"
                    class="preset-btn"
                    @click="applyPreset(preset.key)"
                  >
                    {{ preset.label }}
                  </b-button>
                </b-button-group>
              </div>
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col>
              <span class="range-summary">
                <feather-icon
                  icon="CalendarIcon"
                  size="14"
                  class="mr-1"
                />
                {{ dateRangeText }}
              </span>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </b-container>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="loading-container"
    >
      <div class="loading-spinner">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
        <p class="loading-text">
          Chargement des données analytiques...
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="analytics-content"
    >
      <!-- Summary KPI Cards -->
      <div class="kpi-grid">
        <div class="kpi-card total-orders">
          <div class="kpi-icon">
            <feather-icon
              icon="PackageIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatNumber(summary.total_orders) }}
            </h3>
            <p class="kpi-label">
              Total Commandes
            </p>
          </div>
          <div class="kpi-trend">
            <feather-icon
              :icon="summary.trend_direction === 'up' ? 'TrendingUpIcon' : summary.trend_direction === 'down' ? 'TrendingDownIcon' : 'MinusIcon'"
              size="16"
              :class="getTrendClass(summary.trend_direction)"
            />
            <span :class="getTrendClass(summary.trend_direction)">{{ summary.trend_percentage }}%</span>
          </div>
        </div>

        <div class="kpi-card delivered">
          <div class="kpi-icon success">
            <feather-icon
              icon="CheckCircleIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatNumber(summary.total_delivered) }}
            </h3>
            <p class="kpi-label">
              Livrées
            </p>
            <div class="kpi-subvalue">
              {{ summary.overall_delivery_rate }}% Taux de livraison
            </div>
          </div>
        </div>

        <div class="kpi-card returned">
          <div class="kpi-icon danger">
            <feather-icon
              icon="RotateCcwIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatNumber(summary.total_returned) }}
            </h3>
            <p class="kpi-label">
              Retours
            </p>
            <div class="kpi-subvalue warning">
              {{ summary.overall_return_rate }}% Taux de retour
            </div>
          </div>
        </div>

        <div class="kpi-card revenue">
          <div class="kpi-icon primary">
            <feather-icon
              icon="DollarSignIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatCurrency(summary.total_revenue) }}
            </h3>
            <p class="kpi-label">
              Chiffre d'Affaires
            </p>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <!-- Performance Distribution Chart -->
        <div class="chart-card">
          <div class="chart-header">
            <h3 class="chart-title">
              <feather-icon
                icon="PieChartIcon"
                size="18"
              />
              Distribution des Performances
            </h3>
          </div>
          <div class="chart-body">
            <div class="performance-distribution">
              <div
                v-for="(count, level) in distribution"
                :key="level"
                class="distribution-item"
              >
                <div class="distribution-bar-container">
                  <div
                    class="distribution-bar"
                    :class="getDistributionClass(level)"
                    :style="{ width: getDistributionWidth(count) + '%' }"
                  />
                </div>
                <div class="distribution-info">
                  <span class="distribution-label">{{ getDistributionLabel(level) }}</span>
                  <span
                    class="distribution-count"
                    :class="getDistributionClass(level)"
                  >{{ count }} régions</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Top Performing Governorates -->
        <div class="chart-card">
          <div class="chart-header">
            <h3 class="chart-title">
              <feather-icon
                icon="AwardIcon"
                size="18"
              />
              Top Gouvernorats Performants
            </h3>
          </div>
          <div class="chart-body">
            <div class="top-performers">
              <div
                v-for="(gov, index) in topPerforming"
                :key="gov.id"
                class="performer-item"
              >
                <div class="performer-rank">
                  <span
                    class="rank-badge"
                    :class="getRankClass(index)"
                  >{{ index + 1 }}</span>
                </div>
                <div class="performer-info">
                  <h4 class="performer-name">
                    {{ gov.name }}
                  </h4>
                  <div class="performer-stats">
                    <span class="stat-item">
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="12"
                      />
                      {{ gov.delivered_count }} livrées
                    </span>
                    <span class="stat-item success">
                      <feather-icon
                        icon="TrendingUpIcon"
                        size="12"
                      />
                      {{ gov.delivery_rate }}%
                    </span>
                  </div>
                </div>
                <div class="performer-bar">
                  <div
                    class="performer-progress"
                    :style="{ width: gov.delivery_rate + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div> <!-- Closing the last div properly -->
      </div> <!-- Ensure this div is correctly closed -->

      <!-- Detailed Table Section -->
      <div class="table-section">
        <div class="table-card">
          <div class="table-header">
            <div class="table-title-section">
              <h3 class="table-title">
                <feather-icon
                  icon="ListIcon"
                  size="18"
                />
                Détails par Gouvernorat
              </h3>
              <span class="table-count">{{ governorates.length }} gouvernorats actifs</span>
            </div>
            <div class="table-actions">
              <b-form-input
                v-model="searchQuery"
                placeholder="Rechercher un gouvernorat..."
                size="sm"
                class="search-input"
              />
              <b-button-group size="sm">
                <b-button
                  :variant="sortBy === 'total_orders' ? 'primary' : 'outline-primary'"
                  @click="sortTable('total_orders')"
                >
                  Commandes
                </b-button>
                <b-button
                  :variant="sortBy === 'delivery_rate' ? 'primary' : 'outline-primary'"
                  @click="sortTable('delivery_rate')"
                >
                  Performance
                </b-button>
                <b-button
                  :variant="sortBy === 'return_rate' ? 'primary' : 'outline-primary'"
                  @click="sortTable('return_rate')"
                >
                  Retours
                </b-button>
              </b-button-group>
            </div>
          </div>
          <div class="table-body">
            <div class="responsive-table">
              <table class="analytics-table">
                <thead>
                  <tr>
                    <th class="th-governorate">
                      Gouvernorat
                    </th>
                    <th class="th-center">
                      Total Commandes
                    </th>
                    <th class="th-center">
                      Livrées
                    </th>
                    <th class="th-center">
                      Retours
                    </th>
                    <th class="th-center">
                      Taux Livraison
                    </th>
                    <th class="th-center">
                      Taux Retour
                    </th>
                    <th class="th-center">
                      % du Total
                    </th>
                    <th class="th-right">
                      Revenus
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="gov in filteredGovernorates"
                    :key="gov.id"
                    class="table-row"
                    :class="{ 'highlight-row': gov.delivery_rate >= 80 }"
                  >
                    <td class="td-governorate">
                      <div class="gov-cell">
                        <div class="gov-avatar">
                          {{ gov.name.charAt(0) }}
                        </div>
                        <div class="gov-info">
                          <span class="gov-name">{{ gov.name }}</span>
                          <span class="gov-delegations">{{ gov.num_delegations }} délégations</span>
                        </div>
                      </div>
                    </td>
                    <td class="td-center">
                      <span class="value-primary">{{ formatNumber(gov.total_orders) }}</span>
                    </td>
                    <td class="td-center">
                      <span class="value-success">{{ formatNumber(gov.delivered_count) }}</span>
                    </td>
                    <td class="td-center">
                      <span class="value-danger">{{ formatNumber(gov.returned_count) }}</span>
                    </td>
                    <td class="td-center">
                      <div class="rate-cell">
                        <div class="rate-bar-container">
                          <div
                            class="rate-bar success"
                            :style="{ width: gov.delivery_rate + '%' }"
                          />
                        </div>
                        <span
                          class="rate-value"
                          :class="getDeliveryRateClass(gov.delivery_rate)"
                        >{{ gov.delivery_rate }}%</span>
                      </div>
                    </td>
                    <td class="td-center">
                      <div class="rate-cell">
                        <div class="rate-bar-container">
                          <div
                            class="rate-bar danger"
                            :style="{ width: gov.return_rate + '%' }"
                          />
                        </div>
                        <span
                          class="rate-value"
                          :class="getReturnRateClass(gov.return_rate)"
                        >{{ gov.return_rate }}%</span>
                      </div>
                    </td>
                    <td class="td-center">
                      <span class="percentage-badge">{{ gov.order_percentage }}%</span>
                    </td>
                    <td class="td-right">
                      <span class="revenue-value">{{ formatCurrency(gov.total_revenue) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Insights Section -->
      <div class="insights-section">
        <div class="insights-card">
          <div class="insights-header">
            <feather-icon
              icon="ZapIcon"
              size="20"
            />
            <h3 class="insights-title">
              Insights & Recommandations
            </h3>
          </div>
          <div class="insights-body">
            <div class="insights-grid">
              <div class="insight-item success">
                <div class="insight-icon success">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="18"
                  />
                </div>
                <div class="insight-content">
                  <h4 class="insight-title">
                    Meilleure Performance
                  </h4>
                  <p class="insight-text">
                    {{ topPerforming.length > 0 ? topPerforming[0].name : 'N/A' }} avec un taux de {{ topPerforming.length > 0 ? topPerforming[0].delivery_rate : 0 }}%
                  </p>
                </div>
              </div>
              <div class="insight-item warning">
                <div class="insight-icon warning">
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="18"
                  />
                </div>
                <div class="insight-content">
                  <h4 class="insight-title">
                    Point d'Amélioration
                  </h4>
                  <p class="insight-text">
                    {{ needsAttention.length }} région(s) avec taux de retour élevé (>20%)
                  </p>
                </div>
              </div>
              <div class="insight-item info">
                <div class="insight-icon info">
                  <feather-icon
                    icon="MapIcon"
                    size="18"
                  />
                </div>
                <div class="insight-content">
                  <h4 class="insight-title">
                    Couverture
                  </h4>
                  <p class="insight-text">
                    {{ summary.active_governorates }} gouvernorats actifs sur {{ governorates.length }}
                  </p>
                </div>
              </div>
              <div
                class="insight-item"
                :class="summary.trend_direction === 'up' ? 'success' : 'danger'"
              >
                <div
                  class="insight-icon"
                  :class="summary.trend_direction === 'up' ? 'success' : 'danger'"
                >
                  <feather-icon
                    :icon="summary.trend_direction === 'up' ? 'ArrowUpIcon' : 'ArrowDownIcon'"
                    size="18"
                  />
                </div>
                <div class="insight-content">
                  <h4 class="insight-title">
                    Tendance 7 Jours
                  </h4>
                  <p class="insight-text">
                    {{ summary.trend_direction === 'up' ? 'Hausse' : 'Baisse' }} de {{ Math.abs(summary.trend_percentage) }}% vs semaine précédente
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BButton,
  BFormInput,
  BButtonGroup,
  BCard,
  BCardBody,
  BFormGroup,
  BFormDatepicker,
  BRow,
  BCol,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'
import dayjs from 'dayjs'

export default {
  name: 'GovernorateAnalytics',
  components: {
    BSpinner,
    BButton,
    BFormInput,
    BButtonGroup,
    BCard,
    BCardBody,
    BFormGroup,
    BFormDatepicker,
    BRow,
    BCol,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      summary: {
        total_orders: 0,
        total_delivered: 0,
        total_returned: 0,
        total_revenue: 0,
        overall_delivery_rate: 0,
        overall_return_rate: 0,
        active_governorates: 0,
        trend_percentage: 0,
        trend_direction: 'stable',
      },
      governorates: [],
      topPerforming: [],
      needsAttention: [],
      distribution: {
        excellent: 0,
        good: 0,
        average: 0,
        poor: 0,
      },
      fromDate: null,
      toDate: null,
      searchQuery: '',
      sortBy: 'total_orders',
      sortDesc: true,
      presets: [
        { label: "Aujourd'hui", key: 'today' },
        { label: '7 derniers jours', key: 'last7' },
        { label: '30 derniers jours', key: 'last30' },
        { label: 'Ce mois', key: 'thisMonth' },
        { label: 'Mois dernier', key: 'lastMonth' },
      ],
    }
  },
  computed: {
    dateRangeText() {
      if (this.fromDate && this.toDate) {
        return `${dayjs(this.fromDate).format('DD/MM/YYYY')} - ${dayjs(this.toDate).format('DD/MM/YYYY')}`
      }
      return 'Période non définie'
    },
    filteredGovernorates() {
      let filtered = [...this.governorates]

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(gov => gov.name.toLowerCase().includes(query))
      }

      // Sort
      filtered.sort((a, b) => {
        const aVal = a[this.sortBy]
        const bVal = b[this.sortBy]
        if (this.sortDesc) {
          return bVal - aVal
        }
        return aVal - bVal
      })

      return filtered
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.applyPreset('thisMonth')
  },
  methods: {
    async fetchAnalytics() {
      try {
        if (!this.fromDate || !this.toDate) {
          return
        }

        const start = dayjs(this.fromDate)
        const end = dayjs(this.toDate)
        if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
          this.$bvToast.toast('La date de début doit être antérieure ou égale à la date de fin.', {
            title: 'Date invalide',
            variant: 'warning',
            solid: true,
          })
          return
        }

        this.isLoading = true
        const url = `/api/kpi/governorate-analytics/?start_date=${this.fromDate}&end_date=${this.toDate}`
        const { data } = await axios.get(url)

        this.summary = data.summary || this.summary
        this.governorates = data.governorates || []
        this.topPerforming = data.top_performing || []
        this.needsAttention = data.needs_attention || []
        this.distribution = data.distribution || this.distribution
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching governorate analytics:', error)
        this.$bvToast.toast('Erreur lors du chargement des données', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoading = false
      }
    },
    applyPreset(preset) {
      const today = dayjs()
      switch (preset) {
        case 'today':
          this.fromDate = today.format('YYYY-MM-DD')
          this.toDate = today.format('YYYY-MM-DD')
          break
        case 'last7':
          this.fromDate = today.subtract(6, 'day').format('YYYY-MM-DD')
          this.toDate = today.format('YYYY-MM-DD')
          break
        case 'last30':
          this.fromDate = today.subtract(29, 'day').format('YYYY-MM-DD')
          this.toDate = today.format('YYYY-MM-DD')
          break
        case 'thisMonth':
          this.fromDate = today.startOf('month').format('YYYY-MM-DD')
          this.toDate = today.format('YYYY-MM-DD')
          break
        case 'lastMonth':
          this.fromDate = today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
          this.toDate = today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          break
        default:
          return
      }
      this.fetchAnalytics()
    },
    resetFilters() {
      this.applyPreset('thisMonth')
    },
    formatDateApi(date) {
      return dayjs(date).format('YYYY-MM-DD')
    },
    formatNumber(value) {
      if (!value && value !== 0) return '0'
      return new Intl.NumberFormat('fr-FR').format(value)
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '0.000 TND'
      return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 3 }).format(value)} TND`
    },
    getTrendClass(direction) {
      if (direction === 'up') return 'trend-up'
      if (direction === 'down') return 'trend-down'
      return 'trend-stable'
    },
    getDistributionClass(level) {
      const classes = {
        excellent: 'dist-excellent',
        good: 'dist-good',
        average: 'dist-average',
        poor: 'dist-poor',
      }
      return classes[level] || ''
    },
    getDistributionLabel(level) {
      const labels = {
        excellent: 'Excellent (≥80%)',
        good: 'Bon (60-79%)',
        average: 'Moyen (40-59%)',
        poor: 'Faible (<40%)',
      }
      return labels[level] || level
    },
    getDistributionWidth(count) {
      const total = Object.values(this.distribution).reduce((a, b) => a + b, 0)
      if (total === 0) return 0
      return Math.max((count / total) * 100, 5)
    },
    getRankClass(index) {
      if (index === 0) return 'rank-gold'
      if (index === 1) return 'rank-silver'
      if (index === 2) return 'rank-bronze'
      return 'rank-default'
    },
    getDeliveryRateClass(rate) {
      if (rate >= 80) return 'rate-excellent'
      if (rate >= 60) return 'rate-good'
      if (rate >= 40) return 'rate-average'
      return 'rate-poor'
    },
    getReturnRateClass(rate) {
      if (rate <= 10) return 'rate-excellent'
      if (rate <= 20) return 'rate-good'
      if (rate <= 30) return 'rate-average'
      return 'rate-poor'
    },
    sortTable(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = field
        this.sortDesc = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Modern Animated Title Section with Light Red Background
.title-section {
  position: relative;
  padding: 2rem 2.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #2b0a0a 0%, #3b0f0f 40%, #050505 100%);
  border-radius: 1.25rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 77, 79, 0.25);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 0% 0%, rgba(255, 77, 79, 0.12) 0%, transparent 55%),
      radial-gradient(circle at 100% 100%, rgba(0, 0, 0, 0.25) 0%, transparent 55%);
    animation: backgroundPulse 4s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.25) 50%,
      transparent 70%
    );
    animation: shimmerEffect 6s ease-in-out infinite;
  }
}

@keyframes backgroundPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.02);
  }
}

@keyframes shimmerEffect {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) rotate(45deg);
  }
}

.title-container {
  display: flex;
  align-items: center;
  gap: 1.75rem;
  position: relative;
  z-index: 1;
}

.title-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #9b1c22 0%, #5c0f13 100%);
  border-radius: 1rem;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  animation: iconPulse 3s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 1.15rem;
    background: linear-gradient(135deg, #9b1c22, #5c0f13);
    z-index: -1;
    opacity: 0.55;
    animation: ringGlow 2s ease-in-out infinite;
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
  }
}

@keyframes ringGlow {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
}

.title-icon {
  color: #ffffff;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.title-text-wrapper {
  flex: 1;
}

.main-title {
  font-size: 1.85rem;
  font-weight: 700;
  margin: 0 0 0.6rem;
  color: #f7f7f7;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.title-word {
  display: inline-block;
  color: #f7f7f7;
  animation: fadeSlideIn 0.5s ease forwards;
  opacity: 0;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.15s; }
  &:nth-child(3) { animation-delay: 0.2s; }
  &:nth-child(4) { animation-delay: 0.25s; }
  &:nth-child(5) { animation-delay: 0.3s; }

  &.highlight {
    color: #ffffff;
    font-weight: 800;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, #ffffff, #e2e8f0);
      border-radius: 2px;
      transform: scaleX(0);
      transform-origin: left;
      animation: underlineReveal 0.6s ease forwards 0.5s;
    }
  }
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes underlineReveal {
  to {
    transform: scaleX(1);
  }
}

.subtitle {
  font-size: 1rem;
  color: #e6e7e9;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: fadeSlideIn 0.5s ease forwards 0.4s;
  opacity: 0;
}

.subtitle-icon {
  font-size: 1.1rem;
  animation: subtitleIconBounce 2s ease-in-out infinite;
}

@keyframes subtitleIconBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

// Responsive
@media (max-width: 768px) {
  .title-section {
    padding: 1.5rem 1.25rem;
  }

  .title-container {
    gap: 1rem;
  }

  .title-icon-wrapper {
    width: 52px;
    height: 52px;
  }

  .main-title {
    font-size: 1.4rem;
  }

  .subtitle {
    font-size: 0.9rem;
  }
}

// Filter Section
.filter-card {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);

  .card-body {
    padding: 1.5rem;
  }
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.filter-datepicker {
  &.b-form-btn-label-control {
    min-height: 44px;
    border-radius: 0.75rem;
    border-color: #e2e8f0;
    transition: all 0.2s ease;

    &:focus-within {
      border-color: #9b1c22;
      box-shadow: 0 0 0 3px rgba(155, 28, 34, 0.15);
    }
  }
}

.preset-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.preset-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
}

.preset-btn {
  border-color: #9b1c22;
  color: #9b1c22;
  border-radius: 2rem;
  padding: 0.375rem 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    background: #9b1c22;
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(155, 28, 34, 0.25);
  }
}

.range-summary {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: #334155;
  background: #fef2f2;
  padding: 0.5rem 0.875rem;
  border-radius: 2rem;
  font-weight: 500;
}

.filter-btn {
  min-height: 44px;
  border-radius: 0.75rem;
  font-weight: 600;
}

.governorate-analytics {
  padding: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  min-height: 100vh;
}

// Header Styles
.analytics-header {
  background: linear-gradient(-45deg, #0A2634, #0d3040, #1a4a5e, #0f3545);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  border-radius: 0 0 1.5rem 1.5rem;
  box-shadow: 0 4px 20px rgba(10, 38, 52, 0.35);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 60%);
    animation: shimmer 4s linear infinite;
  }
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes shimmer {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  background: rgba(255, 255, 255, 0.25);
  padding: 0.75rem;
  border-radius: 1rem;
  color: white;
  animation: pulse 2s ease-in-out infinite;
  backdrop-filter: blur(4px);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 20px 5px rgba(255, 255, 255, 0.2); }
}

.header-info {
  color: white;
}

.header-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0.25rem 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.date-filters {
  display: flex;
  gap: 0.5rem;
}

.year-select,
.month-select {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 0.5rem;
  min-width: 100px;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Loading State
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
}

.loading-text {
  margin-top: 1rem;
  color: #6c757d;
  font-size: 0.95rem;
}

// Analytics Content
.analytics-content {
  padding: 0 1.5rem 2rem;
}

// KPI Grid
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.kpi-card {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
  }

  &.total-orders::before { background: linear-gradient(to bottom, #667eea, #764ba2); }
  &.delivered::before { background: linear-gradient(to bottom, #28c76f, #1ea85a); }
  &.returned::before { background: linear-gradient(to bottom, #0A2634, #061a22); }
  &.revenue::before { background: linear-gradient(to bottom, #ff9f43, #f08c2e); }
}

.kpi-icon {
  background: rgba(102, 126, 234, 0.15);
  padding: 0.875rem;
  border-radius: 0.875rem;
  color: #667eea;
  flex-shrink: 0;

  &.success {
    background: #dcfce7;
    color: #3eb569;
  }
  &.danger {
    background: rgba(10, 26, 34, 0.15);
    color: #0a1a22;
  }
  &.primary {
    background: rgba(255, 159, 67, 0.15);
    color: #ff9f43;
  }
}

.kpi-content {
  flex: 1;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.2;
}

.kpi-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0.25rem 0 0;
}

.kpi-subvalue {
  font-size: 0.75rem;
  color: #28c76f;
  margin-top: 0.5rem;
  font-weight: 500;

  &.warning { color: #ff9f43; }
}

.kpi-trend {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.trend-up { color: #28c76f; }
.trend-down { color: #0A2634; }
.trend-stable { color: #6c757d; }

// Charts Section
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.chart-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chart-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chart-body {
  padding: 1.5rem;
}

// Performance Distribution
.performance-distribution {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.distribution-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.distribution-bar-container {
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.distribution-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;

  &.dist-excellent { background: linear-gradient(90deg, #28c76f, #1ea85a); }
  &.dist-good { background: linear-gradient(90deg, #00cfe8, #00b8d4); }
  &.dist-average { background: linear-gradient(90deg, #ff9f43, #f08c2e); }
  &.dist-poor { background: linear-gradient(90deg, #0A2634, #061a22); }
}

.distribution-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.distribution-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.distribution-count {
  font-size: 0.85rem;
  font-weight: 600;

  &.dist-excellent { color: #28c76f; }
  &.dist-good { color: #00cfe8; }
  &.dist-average { color: #ff9f43; }
  &.dist-poor { color: #0A2634; }
}

// Top Performers
.top-performers {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.performer-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f2f5;
  }
}

.performer-rank {
  flex-shrink: 0;
}

.rank-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;

  &.rank-gold {
    background: linear-gradient(135deg, #ffd700, #ffb800);
    color: #5c4a00;
  }
  &.rank-silver {
    background: linear-gradient(135deg, #c0c0c0, #a0a0a0);
    color: #4a4a4a;
  }
  &.rank-bronze {
    background: linear-gradient(135deg, #cd7f32, #b87333);
    color: white;
  }
  &.rank-default {
    background: #e0e0e0;
    color: #666;
  }
}

.performer-info {
  flex: 1;
  min-width: 0;
}

.performer-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.performer-stats {
  display: flex;
  gap: 1rem;
  margin-top: 0.25rem;
}

.stat-item {
  font-size: 0.75rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &.success { color: #28c76f; font-weight: 600; }
}

.performer-bar {
  width: 80px;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.performer-progress {
  height: 100%;
  background: linear-gradient(90deg, #28c76f, #1ea85a);
  border-radius: 3px;
  transition: width 0.6s ease;
}

// Attention Section
.attention-section {
  margin-bottom: 1.5rem;
}

.attention-card {
  background: linear-gradient(135deg, #fff8f3 0%, #fff 100%);
  border: 1px solid #fecaca;
  border-radius: 1rem;
  overflow: hidden;
}

.attention-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: rgba(239, 68, 68, 0.1);
  border-bottom: 1px solid #fecaca;
}

.attention-icon {
  color: #ef4444;
}

.attention-title {
  font-size: 1rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0;
  flex: 1;
}

.attention-badge {
  background: #fef2f2;
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
}

.attention-body {
  padding: 1rem 1.5rem;
}

.attention-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.attention-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #fecaca;
}

.attention-item-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.attention-item-details {
  font-size: 0.8rem;
  color: #6c757d;
  margin: 0.25rem 0 0;
}

.attention-item-rate {
  text-align: right;
}

.rate-value {
  font-size: 1.25rem;
  font-weight: 700;

  &.danger { color: #0A2634; }
}

.rate-label {
  display: block;
  font-size: 0.7rem;
  color: #6c757d;
}

// Table Section
.table-section {
  margin-bottom: 1.5rem;
}

.table-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.table-count {
  font-size: 0.8rem;
  color: #6c757d;
  background: #f0f0f0;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.search-input {
  min-width: 200px;
  border-radius: 0.5rem;
}

.table-body {
  padding: 0;
}

.responsive-table {
  overflow-x: auto;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem 1.25rem;
    text-align: left;
  }

  th {
    background: #f8f9fa;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e9ecef;

    &.th-center { text-align: center; }
    &.th-right { text-align: right; }
  }

  td {
    border-bottom: 1px solid #f0f0f0;

    &.td-center { text-align: center; }
    &.td-right { text-align: right; }
  }
}

.table-row {
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  &.highlight-row {
    background: rgba(40, 199, 111, 0.05);

    &:hover {
      background: rgba(40, 199, 111, 0.1);
    }
  }
}

.gov-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.gov-avatar {
  width: 40px;
  height: 40px;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
}

.gov-info {
  display: flex;
  flex-direction: column;
}

.gov-name {
  font-weight: 600;
  color: #2c3e50;
}

.gov-delegations {
  font-size: 0.75rem;
  color: #6c757d;
}

.value-primary { color: #2c3e50; font-weight: 600; }
.value-success { color: #28c76f; font-weight: 600; }
.value-danger { color: #0A2634; font-weight: 600; }

.rate-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.rate-bar-container {
  width: 60px;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.rate-bar {
  height: 100%;
  border-radius: 3px;

  &.success { background: linear-gradient(90deg, #28c76f, #1ea85a); }
  &.danger { background: linear-gradient(90deg, #0A2634, #061a22); }
}

.rate-value {
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 45px;

  &.rate-excellent { color: #28c76f; }
  &.rate-good { color: #00cfe8; }
  &.rate-average { color: #ff9f43; }
  &.rate-poor { color: #0A2634; }
}

.percentage-badge {
  background: #f0f0f0;
  color: #666;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.revenue-value {
  font-weight: 600;
  color: #ff9f43;
}

// Insights Section
.insights-section {
  margin-bottom: 1.5rem;
}

.insights-card {
  background: #ffffff;
  border-radius: 1rem;
  overflow: hidden;
  color: #000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.insights-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.insights-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #000;
}

.insights-body {
  padding: 1.5rem;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.insight-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 0.75rem;
  backdrop-filter: blur(10px);

  &.success { background: rgba(40, 199, 111, 0.15); }
  &.warning { background: rgba(255, 159, 67, 0.15); }
  &.info { background: rgba(0, 207, 232, 0.15); }
  &.danger { background: rgba(10, 26, 34, 0.15); }
}

.insight-icon {
  padding: 0.5rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.2);

  &.success { background: rgba(40, 199, 111, 0.3); }
  &.warning { background: rgba(255, 159, 67, 0.3); }
  &.info { background: rgba(0, 207, 232, 0.3); }
  &.danger { background: rgba(10, 38, 52, 0.3); }
}

.insight-content {
  flex: 1;
}

.insight-title {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: #000;
}

.insight-text {
  font-size: 0.8rem;
  margin: 0;
  color: #000;
  line-height: 1.4;
}

// Responsive
@media (max-width: 768px) {
  .analytics-header {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .analytics-content {
    padding: 0 1rem 1.5rem;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .table-actions {
    width: 100%;
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }
}
</style>
